import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Dépannage menuiseries Pézenas' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dépannage et réparation de menuiseries à Pézenas',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'City', name: 'Pézenas' },
  description: 'Réparation de baies coulissantes, volets roulants, vitrages, stores et fenêtres à Pézenas. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Réparez-vous les menuiseries anciennes du centre de Pézenas ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, nous intervenons régulièrement sur les fenêtres bois et menuiseries anciennes du centre historique de Pézenas. Nous savons adapter nos réparations aux contraintes du bâti ancien : crémones à l\'ancienne, dimensions hors standard, vitrages simples à remplacer par du double vitrage sur mesure.',
      },
    },
    {
      '@type': 'Question',
      name: 'Quel est votre délai d\'intervention à Pézenas ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Notre délai standard est de 24 à 48h après votre appel, 7 jours sur 7. Nous couvrons tout Pézenas et ses alentours : centre historique, quartiers résidentiels, zone d\'activités. Pour une urgence vitrier, appelez le 09 80 80 22 42.',
      },
    },
  ],
};

export default function PezenasPage() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante Pézenas | DFPRO"
        description="Baie coulissante, fenêtre ancienne ou volet roulant en panne à Pézenas ? DFPRO intervient 24-48h, 7j/7. Devis gratuit, garantie 10 ans. Menuiseries tous types."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/depannage-menuiserie-pezenas/"
      />
      <ServicePageLayout
        heroImage="/images/ville-herault-01.jpg"
        title="Réparation de menuiseries et baie coulissante à Pézenas"
        subtitle="Fenêtres anciennes, baies coulissantes, volets roulants : nos techniciens réparent tous types de menuiseries à Pézenas. Devis gratuit, intervention 24-48h."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        ctaTitle="Menuiserie en panne à Pézenas ?"
        ctaText="Devis gratuit, intervention 24-48h, garantie 10 ans. Nos techniciens interviennent dans tout Pézenas, centre ancien comme quartiers résidentiels."
        ctaServiceLabel="Tous nos services"
      >
        <p className="text-lg text-secondary-500 leading-relaxed">
          Pézenas compte de nombreuses menuiseries anciennes en bois qui demandent un savoir-faire spécifique. Nous intervenons aussi sur les équipements récents : baies coulissantes, volets roulants électriques, vitrages modernes. Diagnostic gratuit au <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a>.
        </p>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/pezenas-baie-01.jpg"
              alt="Réparation de baie vitrée coulissante à Pézenas"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Baie coulissante : remplacement roulettes et galets</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Même dans l'arrière-pays, les baies coulissantes s'usent. Une baie qui <Link to="/baie-coulissante-qui-coince-herault/" className="text-accent-500 font-semibold hover:underline">coince</Link> signale des roulettes à changer. La chaleur estivale peut aussi déformer le rail. Nous intervenons avant que le vantail ne déraille.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">Roulettes et galets</Link>, <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">rail</Link>, <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage</Link> et <Link to="/remplacement-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">remplacement</Link>. Pièces adaptées à toutes les marques.
            </p>
            <Link to="/reparation-baie-coulissante-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage baie coulissante <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Fenêtre bois et PVC : réparation sur-mesure</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Le centre ancien de Pézenas abrite de nombreuses fenêtres en bois aux dimensions atypiques. <Link to="/changer-cremone-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">Crémone</Link> grippée, <Link to="/changer-joint-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">joints</Link> qui ne sont plus étanches, <Link to="/changer-poignee-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">poignée</Link> cassée : nous réparons avec des pièces adaptées au bâti ancien.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Pour les constructions récentes, nous intervenons sur tous les types de <Link to="/reparation-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">fenêtres PVC et alu</Link>, <Link to="/reglage-oscillo-battant-herault/" className="text-accent-500 font-semibold hover:underline">oscillo-battants</Link> et <Link to="/reparation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">volets roulants</Link>.
            </p>
            <Link to="/reparation-fenetre-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage fenêtre <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/pezenas-volet-02.jpg"
              alt="Réparation de fenêtre à Pézenas"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/pezenas-vitrage-03.webp"
              alt="Remplacement de vitrage à Pézenas"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Vitrage et volet roulant : modernisation à Pézenas</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Remplacer un simple vitrage par du <Link to="/remplacement-double-vitrage-herault/" className="text-accent-500 font-semibold hover:underline">double vitrage</Link> dans une menuiserie ancienne améliore considérablement l'isolation. Notre service <Link to="/vitrier-herault/" className="text-accent-500 font-semibold hover:underline">vitrier</Link> réalise cette opération en conservant le cadre d'origine quand c'est possible.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Pour les <Link to="/reparation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">volets roulants</Link> : <Link to="/volet-roulant-bloque-herault/" className="text-accent-500 font-semibold hover:underline">déblocage</Link>, <Link to="/reparation-moteur-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">moteur</Link>, <Link to="/motorisation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link> et <Link to="/changer-sangle-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">sangle</Link>.
            </p>
            <Link to="/vitrier-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Service vitrier <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <h2>Intervention à Pézenas sous 24 à 48h</h2>
        <p>
          Nous couvrons tout Pézenas : centre ancien, quartiers résidentiels, zone d'activités. Communes voisines : Montagnac, Lézignan-la-Cèbe, Tourbes, Caux. Consultez aussi <Link to="/depannage-menuiserie-meze/" className="text-accent-500 font-semibold hover:underline">Mèze</Link> et <Link to="/depannage-menuiserie-beziers/" className="text-accent-500 font-semibold hover:underline">Béziers</Link>. Retrouvez notre <Link to="/" className="text-accent-500 font-semibold hover:underline">page d'accueil</Link>.
        </p>
      </ServicePageLayout>
    </>
  );
}